import { useState } from 'react'
import { useEditor } from '../store/useEditor'
import type { StrandMetrics } from '../render/scene'
import {
  SECTION_KINDS,
  gateWording,
  isVerified,
  measurementText,
  measurementWording,
  sectionOfRun,
  sectionTotals,
  sectionsOf,
  suggestedFeet,
  type RoofSection,
} from '../lib/sections'
import { Section } from './bits'

/**
 * THE PLACES ON THIS HOUSE, AND WHAT ANYBODY ACTUALLY KNOWS ABOUT THEM.
 *
 * This is the screen the whole first phase is gated on, and the gate is one
 * sentence: an uncertain measurement is never silently treated as a verified
 * one. So every row says which of the three it is, in words rather than a
 * color somebody has to learn, and the summary at the bottom reports the
 * unverified footage SEPARATELY from the total instead of adding them into one
 * confident number.
 *
 * It does not draw anything and it does not price anything. It attaches runs
 * that already exist to places that already exist, which is the join that lets
 * one set of ids run from the drawing through to the materials list.
 */
export default function Sections({ stats }: { stats: StrandMetrics[] }) {
  const doc = useEditor((s) => s.doc)
  const selectedId = useEditor((s) => s.selectedId)
  const selectLayer = useEditor((s) => s.selectLayer)
  const addSection = useEditor((s) => s.addSection)
  const [openId, setOpenId] = useState<string | null>(null)

  const sections = sectionsOf(doc)
  const totals = sectionTotals(sections)
  const gate = gateWording(totals)

  return (
    <Section
      title="Places on the house"
      right={
        /*
         * The add control is in the HEADER, not under the list. An open
         * section is a tall column, so a button below it is off the bottom of
         * a phone by the time anybody wants the next one.
         */
        <button className="btn tiny" onClick={() => setOpenId(addSection())}>
          Add a section
        </button>
      }
    >
      {sections.length === 0 && (
        <p className="hint" style={{ marginTop: 0 }}>
          A section is a place: the front eave, the garage rake, the porch ridge. Add one for each
          part you are lighting, then attach the runs you drew on it.
        </p>
      )}

      {/*
        THE ONE SENTENCE, ABOVE THE LIST.

        What still stands between this job and a number somebody can put their
        name on. It is at the top because a person opening this on a phone in a
        driveway should not have to scroll past four open sections to find out
        the answer is "go measure the garage".
      */}
      {sections.length > 0 && (
        <p className={`sec-gate sec-gate-top ${gate ? 'warn' : 'ok'}`}>
          {gate ?? 'Every included section has been measured at the house.'}
        </p>

      )}

      <div className="secs">
        {sections.map((sec, i) => (
          <Row
            key={sec.id}
            sec={sec}
            index={i}
            stats={stats}
            open={openId === sec.id}
            onToggle={() => setOpenId(openId === sec.id ? null : sec.id)}
            selectedId={selectedId}
            selectLayer={selectLayer}
          />
        ))}
      </div>

      {sections.length > 0 && (
        <div className="sec-sum">
          <div className="readout">
            <div>
              <span className="k">In the job</span>
              <span className="v">{totals.included}</span>
            </div>
            <div>
              <span className="k">Total</span>
              <span className="v">{totals.feet} ft</span>
              {/*
                The unverified figure hangs off the total and is never folded
                into it. A single number mixing a walked measurement with a
                guess off a photograph is the exact thing this phase exists to
                stop.

                It used to be a second red sentence under the readout, saying
                the same number the red sentence above the list had already
                said, seven inches apart. Two alarms about one fact is one
                alarm nobody reads. This one is quiet, because it is not
                telling anybody to do something; it is qualifying the figure
                it sits under.
              */}
              {totals.unverifiedFeet > 0 && (
                <em>{Math.round(totals.unverifiedFeet)} ft not measured</em>
              )}
            </div>
          </div>

        </div>
      )}
    </Section>
  )
}

function Row({
  sec,
  index,
  stats,
  open,
  onToggle,
  selectedId,
  selectLayer,
}: {
  sec: RoofSection
  index: number
  stats: StrandMetrics[]
  open: boolean
  onToggle: () => void
  selectedId: string | null
  selectLayer: (id: string | null) => void
}) {
  const doc = useEditor((s) => s.doc)
  const renameSection = useEditor((s) => s.renameSection)
  const setSectionKind = useEditor((s) => s.setSectionKind)
  const setSectionExcluded = useEditor((s) => s.setSectionExcluded)
  const removeSection = useEditor((s) => s.removeSection)
  const attach = useEditor((s) => s.attachRunToSection)
  const detach = useEditor((s) => s.detachRunFromSection)
  const estimate = useEditor((s) => s.estimateSection)
  const verify = useEditor((s) => s.verifySection)

  const [feet, setFeet] = useState('')
  const [by, setBy] = useState('')
  const [how, setHow] = useState('')
  const [showHistory, setShowHistory] = useState(false)

  const sections = sectionsOf(doc)
  const verified = isVerified(sec.measurement)
  // What the drawing itself would say, when the photo has a scale on it. An
  // offer to type, never a number that arrives on its own.
  const drawn = suggestedFeet(sec, stats)
  const name = sec.label.trim() || `Section ${index + 1}`

  const commitEstimate = (source: 'estimated' | 'calibrated') => {
    const n = Number(feet)
    if (!Number.isFinite(n) || n <= 0) return
    estimate(sec.id, { feet: n, source })
    setFeet('')
  }

  const commitVerify = () => {
    const n = Number(feet)
    if (!Number.isFinite(n) || n <= 0 || !by.trim()) return
    verify(sec.id, { feet: n, by: by.trim(), evidence: how.trim() })
    setFeet('')
    setHow('')
  }

  return (
    <div className={`sec${sec.excluded ? ' off' : ''}${open ? ' active' : ''}`}>
      {/*
        THE ROW THE PROTOTYPE DRAWS. A numbered marker, because a crew says
        "run three" and not "the garage rake one"; the name over the detail
        line that identifies it; and the state as a pill on the right, where
        the eye is already going down a column of them.
      */}
      <button className="section-row" onClick={onToggle} aria-expanded={open}>
        <span className="section-name">
          <span className="section-marker">{String(index + 1).padStart(2, '0')}</span>
          <span>
            <strong>{name}</strong>
            {/*
              The kind only when there is one. A section saved before kinds
              existed has none, and printing "Other" for that says the person
              chose Other. Nobody chose anything.
            */}
            <small>
              {[
                sec.measurement.feet > 0 ? measurementText(sec.measurement) : 'No length',
                SECTION_KINDS.find((k) => k.id === sec.kind && k.id !== 'other')?.label,
              ]
                .filter(Boolean)
                .join('  ·  ')}
            </small>

          </span>
        </span>
        <span className={sec.excluded ? 'badge neutral' : verified ? 'badge' : 'badge warn'}>
          {sec.excluded ? 'Left out' : verified ? 'Verified' : 'Check length'}
        </span>
      </button>

      {open && (
        <div className="sec-body">
          <label className="field">
            <span className="field-head">Name</span>
            <input
              className="text"
              value={sec.label}
              placeholder={name}
              onChange={(e) => renameSection(sec.id, e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-head">What it is</span>
            <select
              className="text"
              value={sec.kind}
              onChange={(e) => setSectionKind(sec.id, e.target.value as RoofSection['kind'])}
            >
              {SECTION_KINDS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>

          {/* ---------------------------------------------- the runs on it */}
          <div className="field">
            <span className="field-head">Runs here</span>
            {doc.layers.length === 0 ? (
              <p className="hint" style={{ margin: 0 }}>
                Nothing has been drawn yet.
              </p>
            ) : (
              <div className="sec-runs">
                {doc.layers.map((l, n) => {
                  const owner = sectionOfRun(sections, l.id)
                  const mine = owner?.id === sec.id
                  const taken = !!owner && !mine
                  const color = l.palette?.[0] ?? '#FFB55A'
                  return (
                    <label
                      key={l.id}
                      className={`sec-run${taken ? ' taken' : ''}${l.id === selectedId ? ' on' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={mine}
                        onChange={() => (mine ? detach(l.id) : attach(sec.id, l.id))}
                      />
                      {/*
                        SEVEN RUNS ON A HOUSE ARE ALL CALLED "Roof run".
                        The number is what tells two rows apart in text, and
                        pressing the row lights that one up on the photograph,
                        which is what tells them apart properly.
                      */}
                      <span className="swatch" style={{ background: color, color }} />
                      <span
                        className="sec-run-name"
                        role="button"
                        tabIndex={0}
                        aria-label={`Show ${l.name} ${n + 1} on the photo`}
                        onClick={(e) => {
                          e.preventDefault()
                          selectLayer(l.id === selectedId ? null : l.id)
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return
                          e.preventDefault()
                          selectLayer(l.id === selectedId ? null : l.id)
                        }}
                      >
                        {l.name} {n + 1}
                      </span>
                      {/* A run belongs to one place. Saying where it already
                          is beats silently moving it. */}
                      {taken && (
                        <span className="sec-run-where">
                          on {owner.label.trim() || 'another section'}
                        </span>
                      )}
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          {/* ------------------------------------------------- the length */}
          <div className="field">
            <span className="field-head">Length</span>
            <p className="sec-now">
              {sec.measurement.feet > 0 ? (
                <>
                  <strong>{measurementText(sec.measurement)}</strong>{' '}
                  <span className="hint">{measurementWording(sec.measurement)}</span>
                </>
              ) : (
                <span className="hint">Nobody has put a length on this yet.</span>
              )}
            </p>

            <input
              className="text"
              inputMode="decimal"
              value={feet}
              placeholder={drawn !== null ? `${drawn} from the drawing` : 'Feet'}
              onChange={(e) => setFeet(e.target.value)}
            />
            {drawn !== null && (
              <button className="btn ghost tiny" onClick={() => setFeet(String(drawn))}>
                Use {drawn} ft from the drawing
              </button>
            )}

            {/*
              THREE STATES, AND THE WORDS ARE THE WHOLE POINT.

              Estimated, scaled from a reference, measured at the house. The
              first two are guesses of different quality and the third is a
              fact; a screen that offered one Save button would be deciding
              which of the three this was on the person's behalf.
            */}
            <span className="field-head">Save this length as</span>
            <div className="sec-save">
              <button className="btn out" onClick={() => commitEstimate('estimated')} disabled={!feet}>
                An estimate
              </button>
              <button className="btn out" onClick={() => commitEstimate('calibrated')} disabled={!feet}>
                Scaled from the photo
              </button>
            </div>

            {/*
              Measured at the house is a different act, so it is a different
              control with its own required field. A length with nobody's name
              on it is not a verification, and there is no path in the store
              that would accept one.
            */}
            <div className="sec-verify">
              <span className="field-head">Measured at the house</span>
              <input
                className="text"
                value={by}
                placeholder="Who measured it"
                onChange={(e) => setBy(e.target.value)}
              />
              <input
                className="text"
                value={how}
                placeholder="Wheel, laser, tape"
                onChange={(e) => setHow(e.target.value)}
              />
              <button className="btn wide" onClick={commitVerify} disabled={!by.trim() || !feet}>
                Save measurement
              </button>
            </div>
          </div>

          {sec.history.length > 0 && (
            <div className="field">
              <button className="btn ghost tiny" onClick={() => setShowHistory(!showHistory)}>
                {showHistory
                  ? 'Hide what it was before'
                  : `What it was before (${sec.history.length})`}
              </button>
              {showHistory && (
                <ul className="sec-hist">
                  {[...sec.history].reverse().map((h, k) => (
                    <li key={`${h.at}-${k}`}>
                      <strong>{measurementText(h.was)}</strong> {measurementWording(h.was)}
                      {h.by ? `, replaced by ${h.by}` : ', replaced'} on{' '}
                      {new Date(h.at).toLocaleDateString()}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="sec-foot">
            <button
              className="btn out"
              onClick={() => setSectionExcluded(sec.id, !sec.excluded)}
            >
              {sec.excluded ? 'Put back in the job' : 'Leave this out'}
            </button>
            {/*
              Quiet on purpose. Leaving a section out is the reversible thing a
              person actually wants nine times out of ten, and it is the louder
              of the two controls for that reason.
            */}
            <button className="btn ghost danger" onClick={() => removeSection(sec.id)}>
              Delete
            </button>
          </div>
          {sec.runIds.length > 0 && (
            <p className="hint" style={{ margin: 0 }}>
              Deleting removes the place, not the drawing. {sec.runIds.length}{' '}
              {sec.runIds.length === 1 ? 'run stays' : 'runs stay'} on the photo, unattached.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
